"use client";

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  HistoryAction,
  HistoryState,
  HistoryContextType
} from '@/types/history';

// Action types for the reducer
type HistoryReducerAction =
  | { type: 'ADD_ACTION'; payload: HistoryAction }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'CLEAR_HISTORY' };

// Initial state
const initialHistoryState: HistoryState = {
  actions: [],
  currentIndex: -1, // -1 means no actions yet
};

// Reducer function
function historyReducer(state: HistoryState, action: HistoryReducerAction): HistoryState {
  switch (action.type) {
    case 'ADD_ACTION': {
      const newAction = action.payload;

      // Remove any actions after current index (when adding after undo)
      const actionsUpToCurrent = state.actions.slice(0, state.currentIndex + 1);

      // Add the new action
      const newActions = [...actionsUpToCurrent, newAction];

      // Keep max 100 actions (remove oldest)
      const trimmedActions = newActions.length > 100
        ? newActions.slice(-100)
        : newActions;

      return {
        ...state,
        actions: trimmedActions,
        currentIndex: trimmedActions.length - 1,
      };
    }

    case 'UNDO': {
      if (state.currentIndex <= 0) {
        return state; // Can't undo further
      }

      return {
        ...state,
        currentIndex: state.currentIndex - 1,
      };
    }

    case 'REDO': {
      if (state.currentIndex >= state.actions.length - 1) {
        return state; // Can't redo further
      }

      return {
        ...state,
        currentIndex: state.currentIndex + 1,
      };
    }

    case 'CLEAR_HISTORY': {
      return {
        ...state,
        actions: [],
        currentIndex: -1,
      };
    }

    default:
      return state;
  }
}

// Generate unique IDs for actions
function generateActionId(): string {
  return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Context
const HistoryContext = createContext<HistoryContextType | null>(null);

// Provider component
interface HistoryProviderProps {
  children: React.ReactNode;
}

export function HistoryProvider({
  children
}: HistoryProviderProps) {
  const [state, dispatch] = useReducer(historyReducer, initialHistoryState);

  // localStorage persistence removed - history is now in-memory only
  // This prevents storage explosion issues with base64 images


  // Context methods
  const addAction = useCallback((actionData: Omit<HistoryAction, 'id' | 'timestamp'>) => {
    const newAction: HistoryAction = {
      ...actionData,
      id: generateActionId(),
      timestamp: Date.now(),
    };

    dispatch({ type: 'ADD_ACTION', payload: newAction });
  }, []);

  const undo = useCallback((): HistoryAction | null => {
    if (state.currentIndex <= 0) {
      return null;
    }

    const previousAction = state.actions[state.currentIndex - 1];
    dispatch({ type: 'UNDO' });
    return previousAction;
  }, [state.currentIndex, state.actions]);

  const redo = useCallback((): HistoryAction | null => {
    if (state.currentIndex >= state.actions.length - 1) {
      return null;
    }

    const nextAction = state.actions[state.currentIndex + 1];
    dispatch({ type: 'REDO' });
    return nextAction;
  }, [state.currentIndex, state.actions]);

  const getCurrentImage = useCallback((): string | null => {
    if (state.currentIndex < 0 || state.currentIndex >= state.actions.length) {
      return null;
    }

    return state.actions[state.currentIndex].imageData;
  }, [state.currentIndex, state.actions]);

  const clearHistory = useCallback(() => {
    dispatch({ type: 'CLEAR_HISTORY' });
  }, []);

  const dumpHistory = useCallback(() => {
    console.log('📷 Editor History:', {
      totalActions: state.actions.length,
      currentIndex: state.currentIndex,
      actions: state.actions.map(action => ({
        id: action.id,
        type: action.type,
        timestamp: new Date(action.timestamp).toLocaleTimeString(),
        toolId: action.toolId,
        toolName: action.toolName,
        imageDataSize: action.imageData.length
      }))
    });
  }, [state]);

  // Computed values
  const canUndo = state.currentIndex > 0;
  const canRedo = state.currentIndex < state.actions.length - 1;

  const contextValue: HistoryContextType = {
    history: state,
    canUndo,
    canRedo,
    addAction,
    undo,
    redo,
    getCurrentImage,
    clearHistory,
    dumpHistory,
  };

  return (
    <HistoryContext.Provider value={contextValue}>
      {children}
    </HistoryContext.Provider>
  );
}

// Custom hook
export function useHistory(): HistoryContextType {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }
  return context;
}
